import { Box, Checkbox, Dialog, DialogContent, FormControlLabel, TextField, Typography } from "@mui/material"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import { BoxFC, BoxFR } from "components/BoxCustom"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { alertError, alertSuccess } from "components/Alert"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { numberFormat } from "utils"

/**
 * Pick wage lines that are still ค้างจ่าย — entered where the work is, as their own
 * pc_transaction in PCJnlId 100 — into this driver's งวด. Claiming only points the line at the
 * payroll (RefPCTrnsId); the line stays in its own document, so no amount is duplicated.
 */
const ClaimAccrualDialog = ({ dialogOpen, setDialogOpen, drvId, drvNm, salDte, payrollId, onFinish }) => {
  const { ax } = useContext(GlobalStateContext)
  const [dataTable, setDataTable] = useState([])
  const [selectionModel, setSelectionModel] = useState([])
  const [accrTmEn, setAccrTmEn] = useState(null)
  // ค้างจ่าย carries the งวด it was filed into, so the default question is "what belongs to this
  // งวด" — untick to go looking for older lines that were never given one
  const [onlyThisSalDte, setOnlyThisSalDte] = useState(true)

  const getData = useCallback(() => {
    ax.post("/branch/driver-payroll-v3/getUnclaimedAccruals", {
      DrvId: drvId,
      SalDte: onlyThisSalDte ? dayjs(salDte).format("YYYY-MM-DD") : null,
      AccrTmSt: (!onlyThisSalDte && accrTmEn) ? "2000-01-01" : null,
      AccrTmEn: (!onlyThisSalDte && accrTmEn) ? dayjs(accrTmEn).format("YYYY-MM-DD") : null,
    }).then(value => {
      if (value.data) { setDataTable(value.data); setSelectionModel([]) }
    })
  }, [ax, drvId, accrTmEn, onlyThisSalDte, salDte])

  useEffect(() => {
    if (dialogOpen) { setAccrTmEn(dayjs(salDte).endOf("month")); setOnlyThisSalDte(true) }
  }, [dialogOpen, salDte])

  useEffect(() => { if (dialogOpen) getData() }, [dialogOpen, getData])

  const selectedTotal = useMemo(() => dataTable
    .filter(r => selectionModel.includes(r.id))
    .reduce((s, r) => s + (r.InOutTyp === "I" ? -1 : 1) * +r.Amnt, 0), [dataTable, selectionModel])

  const handleClaim = useCallback(() => {
    if (selectionModel.length === 0) { alertError("กรุณาเลือกรายการที่ต้องการดึงเข้างวด"); return }
    ax.post("/branch/driver-payroll-v3/claimWageItems", {
      PayrollId: payrollId || null,
      DrvId: drvId,
      SalDte: dayjs(salDte).format("YYYY-MM-DD"),
      WgItmIds: selectionModel,
    }).then(value => {
      if (!value.data) return
      const { claimed, skipped, warnings } = value.data
      if (skipped > 0) alertError(`ดึงเข้างวด ${claimed} รายการ ข้าม ${skipped} รายการ\n`
        + warnings.map(w => `- ${w.message}`).join("\n"))
      else alertSuccess(`ดึงเข้างวดแล้ว ${claimed} รายการ`)
      setDialogOpen(false)
      if (onFinish) onFinish()
    })
  }, [ax, selectionModel, payrollId, drvId, salDte, setDialogOpen, onFinish])

  const columns = useMemo(() => [
    {
      field: "StsTm", headerName: "วันที่วิ่งงาน", width: 120,
      valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YY HH:mm") : "-",
    },
    { field: "JobOrdId", headerName: "ใบงาน", width: 80 },
    {
      field: "ExpNm", headerName: "รายการ", flex: 1, minWidth: 160,
      renderCell: ({ row }) => (
        <Typography variant="body2">{row.ExpNm}{row.ContNo ? ` (${row.ContNo})` : ""}</Typography>
      ),
    },
    { field: "JobSNm", headerName: "งาน", width: 110 },
    {
      field: "Amnt", headerName: "จำนวน", width: 90, type: "number", align: "right", headerAlign: "right",
      renderCell: ({ row }) => (
        <Typography variant="body2" color={row.InOutTyp === "I" ? "error.main" : "text.primary"}>
          {numberFormat(row.Amnt)}
        </Typography>
      ),
    },
    {
      field: "SalDte", headerName: "งวดที่จ่าย", width: 90,
      valueFormatter: ({ value }) => value ? dayjs(value).format("MM/YYYY") : "ยังไม่ระบุ",
    },
    {
      field: "AccrTm", headerName: "วันที่ลงค้างจ่าย", width: 130,
      valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YY HH:mm") : "-",
    },
  ], [])

  return (
    <Dialog open={dialogOpen} PaperComponent={PaperComponent} fullWidth maxWidth="lg">
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={`ดึงรายการค้างจ่าย — ${drvNm || ""} เข้างวด ${dayjs(salDte).format("MM/YYYY")}`}
        onDeleteClick={null} />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          <BoxFR sx={{ justifyContent: "flex-end" }}>
            <Box flex={1} />
            <FormControlLabel label={`เฉพาะงวด ${dayjs(salDte).format("MM/YYYY")}`}
              control={<Checkbox size="small" checked={onlyThisSalDte}
                onChange={e => setOnlyThisSalDte(e.target.checked)} />} />
            <DesktopDatePicker label="ค้างจ่ายถึงวันที่" disableMaskedInput inputFormat="DD/MM/YYYY"
              value={accrTmEn} onChange={setAccrTmEn} disabled={onlyThisSalDte}
              renderInput={(params) => <TextField size="small" sx={{ width: 160 }} {...params} />} />
          </BoxFR>

          <Box sx={{ height: 420 }}>
            <DataGridCellExpand
              hideFooter
              density="compact"
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selectionModel}
              onSelectionModelChange={setSelectionModel}
              rows={dataTable}
              columns={columns} />
          </Box>

          <BoxFR sx={{ justifyContent: "flex-end" }}>
            <Typography variant="body2">เลือก {selectionModel.length} รายการ</Typography>
            <Typography variant="subtitle1" fontWeight="bold">{numberFormat(selectedTotal)}</Typography>
          </BoxFR>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="ดึงเข้างวด"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleClaim} />
    </Dialog>
  )
}

export default React.memo(ClaimAccrualDialog)
